import { useEffect, useMemo, useState } from 'react';
import { useQuery, useAction, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import ViolationCard from './ViolationCard';
import ReachabilityBadge from './ReachabilityBadge';
import ExportButton from './ExportButton';
import DMCAEmailModal from './DMCAEmailModal';
import ProgressiveList from './ProgressiveList';
import RegionalBadges, { RegionCode, RegionStatus, normalizeRegionStatus } from './RegionalBadges';
import { getReadablePath } from '../lib/urlUtils';
import './Dashboard.css';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

interface DashboardProps {
  batchId: string;
}

const REGION_CODES: RegionCode[] = ['sa', 'ae', 'eg'];

type SimpleFilterKey =
  | 'clientReachable'
  | 'clientUnreachable'
  | 'serverVerified'
  | 'serverUnverified'
  | 'regionalAnyReachable'
  | 'regionalAllBlocked';

interface FilterState {
  clientReachable: boolean;
  clientUnreachable: boolean;
  serverVerified: boolean;
  serverUnverified: boolean;
  regionalAnyReachable: boolean;
  regionalAllBlocked: boolean;
  regionalPerCountryReachable: Record<RegionCode, boolean>;
  regionalPerCountryBlocked: Record<RegionCode, boolean>;
}

const createDefaultFilters = (): FilterState => ({
  clientReachable: false,
  clientUnreachable: false,
  serverVerified: false,
  serverUnverified: false,
  regionalAnyReachable: false,
  regionalAllBlocked: false,
  regionalPerCountryReachable: {
    sa: false,
    ae: false,
    eg: false,
  },
  regionalPerCountryBlocked: {
    sa: false,
    ae: false,
    eg: false,
  },
});

export default function Dashboard({ batchId }: DashboardProps) {
  const [capturingScreenshots, setCapturingScreenshots] = useState(false);
  const [screenshotProgress, setScreenshotProgress] = useState<string>('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [filterScanId, setFilterScanId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [domainFilter, setDomainFilter] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filters, setFilters] = useState<FilterState>(() => createDefaultFilters());
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [pausedState, setPausedState] = useState<boolean>(() => {
    try { return typeof window !== 'undefined' && window.localStorage.getItem('scanner:paused') === '1'; } catch { return false; }
  });

  const toggleSimpleFilter = (key: SimpleFilterKey) => {
    setFilters(prev => {
      const updates: Partial<FilterState> = {
        [key]: !prev[key],
      };

      // Handle mutually exclusive pairs
      if (key === 'serverVerified') {
        updates.serverUnverified = false;
      } else if (key === 'serverUnverified') {
        updates.serverVerified = false;
      } else if (key === 'regionalAnyReachable') {
        updates.regionalAllBlocked = false;
      } else if (key === 'regionalAllBlocked') {
        updates.regionalAnyReachable = false;
      }

      return { ...prev, ...updates };
    });
  };

  const toggleRegionalReachable = (code: RegionCode) => {
    setFilters(prev => ({
      ...prev,
      regionalPerCountryReachable: {
        ...prev.regionalPerCountryReachable,
        [code]: !prev.regionalPerCountryReachable[code],
      },
      // Auto-unset the blocked filter for this country (mutual exclusivity)
      regionalPerCountryBlocked: {
        ...prev.regionalPerCountryBlocked,
        [code]: false,
      },
    }));
  };

  const toggleRegionalBlocked = (code: RegionCode) => {
    setFilters(prev => ({
      ...prev,
      regionalPerCountryBlocked: {
        ...prev.regionalPerCountryBlocked,
        [code]: !prev.regionalPerCountryBlocked[code],
      },
      // Auto-unset the reachable filter for this country (mutual exclusivity)
      regionalPerCountryReachable: {
        ...prev.regionalPerCountryReachable,
        [code]: false,
      },
    }));
  };

  const resetFilters = () => setFilters(createDefaultFilters());

  const isFilterActive = useMemo(() => {
    const simple =
      filters.clientReachable ||
      filters.clientUnreachable ||
      filters.serverVerified ||
      filters.serverUnverified ||
      filters.regionalAnyReachable ||
      filters.regionalAllBlocked;
    const perCountryReachable = REGION_CODES.some(code => filters.regionalPerCountryReachable[code]);
    const perCountryBlocked = REGION_CODES.some(code => filters.regionalPerCountryBlocked[code]);
    return simple || perCountryReachable || perCountryBlocked;
  }, [filters]);

  const deriveRegionStatus = (items: any[], field: 'reachableSAStatus' | 'reachableAEStatus' | 'reachableEGStatus'): RegionStatus => {
    let candidate: RegionStatus = 'unknown';
    for (const item of items) {
      const status = normalizeRegionStatus(item[field]);
      if (status === 'reachable') {
        return 'reachable';
      }
      if (status === 'blocked') {
        candidate = 'blocked';
      } else if (status === 'error' && candidate === 'unknown') {
        candidate = 'error';
      }
    }
    return candidate;
  };

  const scans = useQuery(api.scans.getScansByBatch, { batchId });
  const violations = useQuery(api.violations.getViolationsByBatch, { batchId });
  const violationsAll = useQuery(api.violations.getViolationsByBatchAll, { batchId });
  const visibilityByBatch: any[] | undefined = useQuery(api.searchVisibility.getVisibilityDataByBatch, { batchId });
  const isLoading = !scans;
  const scansSafe = scans || [] as any[];
  const captureScreenshots = useAction(api.violations.captureScreenshotsForBatch);
  const bulkMarkNotPirate = useMutation(api.violations.markNotPirate);
  const bulkRestore = useMutation(api.violations.restoreViolation);
  const deleteBatch = useMutation(api.scans.deleteBatch);
  const [dmcaDomain, setDmcaDomain] = useState<string | null>(null);

  const handleDeleteBatch = async () => {
    const scanCount = scansSafe.length;
    const confirmed = window.confirm(
      `⚠️ DELETE ENTIRE SCAN BATCH?\n\n` +
      `This will permanently delete:\n` +
      `• ${scanCount} scan${scanCount !== 1 ? 's' : ''}\n` +
      `• ${totalViolations} violation${totalViolations !== 1 ? 's' : ''}\n` +
      `• All visibility data\n\n` +
      `This action CANNOT be undone.\n\n` +
      `Type the batch ID to confirm: ${batchId}`
    );

    if (!confirmed) return;

    const userInput = window.prompt(
      `To confirm deletion, type the batch ID:\n${batchId}`
    );

    if (userInput !== batchId) {
      alert('Batch ID does not match. Deletion cancelled.');
      return;
    }

    try {
      const result = await deleteBatch({ batchId });
      console.log(`✅ Deleted batch: ${(result as any).deletedScans} scans, ${(result as any).deletedViolations} violations, ${(result as any).deletedVisibilityData} visibility records`);
      // Redirect to home after deletion
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to delete batch:', error);
      alert('Failed to delete batch. Please try again.');
    }
  };

  const totalViolations = scansSafe.reduce((sum, scan) => sum + (scan.violationCount || 0), 0);
  const completedScans = scansSafe.filter(s => s.status === 'complete').length;
  const errorScans = scansSafe.filter(s => s.status === 'error').length;

  // Calculate verified vs unverified breakdown for summary
  const verifiedCount = (violations || []).filter((v: any) => v.verified === true).length;
  const unverifiedCount = (violations || []).filter((v: any) => v.verified === false || v.verificationMethod === 'search_only').length;

  // Calculate average visibility score
  const scansWithVisibility = scansSafe.filter(s => s.visibilityScore !== undefined && s.visibilityScore !== null);
  const avgVisibilityScore = scansWithVisibility.length > 0
    ? scansWithVisibility.reduce((sum, s) => sum + (s.visibilityScore || 0), 0) / scansWithVisibility.length
    : null;

  // Load persisted expanded state or initialize defaults when scans load
  useEffect(() => {
    const storageKey = `dashboard:expanded:${batchId}`;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, boolean>;
        setExpanded(parsed);
        return;
      }
      // If arriving from History (flag set), auto-expand all to show results immediately
      const auto = localStorage.getItem('dashboard:autoExpand');
      if (auto === '1') {
        const map: Record<string, boolean> = {};
        for (const s of scansSafe) map[s._id] = true;
        setExpanded(map);
        localStorage.removeItem('dashboard:autoExpand');
        return;
      }
    } catch {}
    // Fallback: initialize defaults
    const map: Record<string, boolean> = {};
    for (const s of scansSafe) map[s._id] = s.status !== 'complete';
    setExpanded(map);
  }, [batchId, scansSafe.map(s => s._id + s.status).join('|')]);

  // Persist expanded state when it changes
  useEffect(() => {
    const storageKey = `dashboard:expanded:${batchId}`;
    try {
      if (Object.keys(expanded).length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(expanded));
      }
    } catch {}
  }, [batchId, expanded]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!domainDropdownOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.domain-filter-dropdown-inline')) {
        setDomainDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [domainDropdownOpen]);

  const setAllExpanded = (open: boolean) => {
    const map: Record<string, boolean> = {};
    for (const s of scansSafe) map[s._id] = open;
    setExpanded(map);
  };

  const exportVisibleCsv = () => {
    try {
      const visibleScanIds = new Set((filteredScans || scansSafe).map((s: any) => String(s._id)));
      const list = (showArchived ? (violationsAll || []) : (violations || [])).filter((v: any) => visibleScanIds.has(String(v.scanId)));
      const df = domainFilter ? domainFilter.toLowerCase() : null;
      const filtered = df ? list.filter((v: any) => String(v.domain).toLowerCase() === df) : list;
      const scanMap = new Map<string, any>(scansSafe.map((s: any) => [String(s._id), s]));
      const rows = filtered.map((v: any) => {
        const s = scanMap.get(String(v.scanId));
        const host = (() => { try { return new URL(v.url).hostname.replace(/^www\./,''); } catch { return ''; } })();
        const reach = (() => { try { return sessionStorage.getItem(`reach:${host}`) || 'unknown'; } catch { return 'unknown'; } })();
        return {
          contentTitle: s?.contentTitle || '',
          url: v.url,
          domain: v.domain,
          confidenceScore: v.confidenceScore,
          verified: v.verified === true ? 'yes' : (v.verified === false ? 'no' : 'n/a'),
          verificationMethod: v.verificationMethod || '',
          detectedAt: new Date(v.detectedAt).toISOString(),
          hostingProvider: v.hostingProvider || '',
          ipAddress: v.ipAddress || '',
          serverLocation: v.serverLocation || '',
          hostingCached: v.hostingCached ? 'yes' : 'no',
          hostingLookupDate: v.hostingLookupDate ? new Date(v.hostingLookupDate).toISOString() : '',
          clientReachability: reach,
          reachableSAStatus: v.reachableSAStatus || '',
          reachableAEStatus: v.reachableAEStatus || '',
          reachableEGStatus: v.reachableEGStatus || '',
          reachableSACheckedAt: v.reachableSACheckedAt ? new Date(v.reachableSACheckedAt).toISOString() : '',
          reachableAECheckedAt: v.reachableAECheckedAt ? new Date(v.reachableAECheckedAt).toISOString() : '',
          reachableEGCheckedAt: v.reachableEGCheckedAt ? new Date(v.reachableEGCheckedAt).toISOString() : '',
        };
      });
      const headers = Object.keys(rows[0] || { contentTitle:'', url:'', domain:'', confidenceScore:'', verified:'', verificationMethod:'', detectedAt:'', hostingProvider:'', ipAddress:'', serverLocation:'', hostingCached:'', hostingLookupDate:'', clientReachability:'', reachableSAStatus:'', reachableAEStatus:'', reachableEGStatus:'', reachableSACheckedAt:'', reachableAECheckedAt:'', reachableEGCheckedAt:'' });
      const escape = (val: any) => {
        const s = String(val ?? '');
        if (s.includes(',') || s.includes('"') || s.includes('\n')) return '"' + s.replace(/"/g,'""') + '"';
        return s;
      };
      const csv = [headers.join(',')].concat(rows.map(r => headers.map(h => escape((r as any)[h])).join(','))).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `visible-${batchId}-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert('Failed to export CSV: ' + (e?.message || 'unknown error'));
    }
  };

  const exportUnverifiedCsv = () => {
    try {
      const list = (showArchived ? (violationsAll || []) : (violations || [])).filter((v: any) => v.verified === false || v.verificationMethod === 'search_only');
      const scanMap = new Map<string, any>(scansSafe.map((s: any) => [String(s._id), s]));
      const rows = list.map((v: any) => {
        const s = scanMap.get(String(v.scanId));
        return {
          contentTitle: s?.contentTitle || '',
          url: v.url,
          domain: v.domain,
          confidenceScore: v.confidenceScore,
          verificationMethod: v.verificationMethod || 'search_only',
          detectedAt: new Date(v.detectedAt).toISOString(),
          hostingProvider: v.hostingProvider || '',
          ipAddress: v.ipAddress || '',
          serverLocation: v.serverLocation || '',
          hostingCached: v.hostingCached ? 'yes' : 'no',
          hostingLookupDate: v.hostingLookupDate ? new Date(v.hostingLookupDate).toISOString() : '',
          reachableSAStatus: v.reachableSAStatus || '',
          reachableAEStatus: v.reachableAEStatus || '',
          reachableEGStatus: v.reachableEGStatus || '',
          reachableSACheckedAt: v.reachableSACheckedAt ? new Date(v.reachableSACheckedAt).toISOString() : '',
          reachableAECheckedAt: v.reachableAECheckedAt ? new Date(v.reachableAECheckedAt).toISOString() : '',
          reachableEGCheckedAt: v.reachableEGCheckedAt ? new Date(v.reachableEGCheckedAt).toISOString() : '',
        };
      });
      const headers = Object.keys(rows[0] || { contentTitle:'', url:'', domain:'', confidenceScore:'', verificationMethod:'', detectedAt:'', hostingProvider:'', ipAddress:'', serverLocation:'', hostingCached:'', hostingLookupDate:'', reachableSAStatus:'', reachableAEStatus:'', reachableEGStatus:'', reachableSACheckedAt:'', reachableAECheckedAt:'', reachableEGCheckedAt:'' });
      const escape = (val: any) => {
        const s = String(val ?? '');
        if (s.includes(',') || s.includes('"') || s.includes('\n')) return '"' + s.replace(/"/g,'""') + '"';
        return s;
      };
      const csv = [headers.join(',')].concat(rows.map(r => headers.map(h => escape((r as any)[h])).join(','))).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `unverified-${batchId}-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const exportDomainCsv = (domain: string, scanId: string) => {
    try {
      const list = currentViolations(scanId).filter((v: any) => String(v.domain).toLowerCase() === String(domain).toLowerCase());
      const scanMap = new Map<string, any>(scansSafe.map((s: any) => [String(s._id), s]));
      const rows = list.map((v: any) => {
        const s = scanMap.get(String(v.scanId));
        return {
          contentTitle: s?.contentTitle || '',
          url: v.url,
          domain: v.domain,
          confidenceScore: v.confidenceScore,
          verified: v.verified === true ? 'yes' : (v.verified === false ? 'no' : 'n/a'),
          verificationMethod: v.verificationMethod || '',
          detectedAt: new Date(v.detectedAt).toISOString(),
          hostingProvider: v.hostingProvider || '',
          ipAddress: v.ipAddress || '',
          serverLocation: v.serverLocation || '',
          hostingCached: v.hostingCached ? 'yes' : 'no',
          hostingLookupDate: v.hostingLookupDate ? new Date(v.hostingLookupDate).toISOString() : '',
          reachableSAStatus: v.reachableSAStatus || '',
          reachableAEStatus: v.reachableAEStatus || '',
          reachableEGStatus: v.reachableEGStatus || '',
          reachableSACheckedAt: v.reachableSACheckedAt ? new Date(v.reachableSACheckedAt).toISOString() : '',
          reachableAECheckedAt: v.reachableAECheckedAt ? new Date(v.reachableAECheckedAt).toISOString() : '',
          reachableEGCheckedAt: v.reachableEGCheckedAt ? new Date(v.reachableEGCheckedAt).toISOString() : '',
        };
      });
      const headers = Object.keys(rows[0] || { contentTitle:'', url:'', domain:'', confidenceScore:'', verified:'', verificationMethod:'', detectedAt:'', hostingProvider:'', ipAddress:'', serverLocation:'', hostingCached:'', hostingLookupDate:'', reachableSAStatus:'', reachableAEStatus:'', reachableEGStatus:'', reachableSACheckedAt:'', reachableAECheckedAt:'', reachableEGCheckedAt:'' });
      const escape = (val: any) => {
        const s = String(val ?? '');
        if (s.includes(',') || s.includes('"') || s.includes('\n')) return '"' + s.replace(/"/g,'""') + '"';
        return s;
      };
      const csv = [headers.join(',')].concat(rows.map(r => headers.map(h => escape((r as any)[h])).join(','))).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `domain-${domain}-${batchId}-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const togglePaused = () => {
    try {
      if (pausedState) {
        localStorage.removeItem('scanner:paused');
        setPausedState(false);
      } else {
        localStorage.setItem('scanner:paused', '1');
        setPausedState(true);
      }
    } catch {}
  };

  const filteredScans = useMemo(() => {
    if (!filterScanId) return scansSafe;
    return scansSafe.filter(s => s._id === filterScanId);
  }, [scansSafe, filterScanId]);

  const currentViolations = (scanId: string) => {
    const list = showArchived ? (violationsAll || []) : (violations || []);
    const byScan = list.filter((v: any) => v.scanId === scanId);
    if (!domainFilter) return byScan;
    const df = domainFilter.toLowerCase();
    return byScan.filter((v: any) => String(v.domain).toLowerCase() === df);
  };

  // Compute domain counts across the whole batch for chips
  const domainCounts = useMemo(() => {
    const list = showArchived ? (violationsAll || []) : (violations || []);
    const counts: Record<string, number> = {};
    for (const v of list) {
      const d = String(v.domain || '').toLowerCase();
      if (!d) continue;
      counts[d] = (counts[d] || 0) + 1;
    }
    // sort by count desc then alphabetically
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    return entries;
  }, [violations, violationsAll, showArchived]);

  const toggleSelected = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const extractRecentDomains = (scan: any): string[] => {
    const text: string = scan.progressText || '';
    if (!text) return [];
    const m = text.match(/Scraping:\s*(.+)$/i);
    if (m && m[1]) {
      return m[1]
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .slice(0, 3);
    }
    return [];
  };

  // Helper function to get score color class
  const getScoreColorClass = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return 'score-unknown';
    if (score >= 90) return 'score-excellent';
    if (score >= 80) return 'score-very-good';
    if (score >= 70) return 'score-good';
    if (score >= 50) return 'score-moderate';
    if (score >= 30) return 'score-poor';
    return 'score-critical';
  };

  // Helper function to get score interpretation
  const getScoreInterpretation = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return 'Not available';
    if (score >= 90) return '✅ Excellent';
    if (score >= 80) return '✅ Very Good';
    if (score >= 70) return '⚠️ Good';
    if (score >= 50) return '⚠️ Moderate';
    if (score >= 30) return '🚨 Poor';
    return '🚨 Critical';
  };

  // Handler to capture screenshots for all violations in batch
  const handleCaptureScreenshots = async () => {
    console.log('[Screenshot] Button clicked - handler started');
    console.log('[Screenshot] Violations:', violations?.length);

    if (!violations) {
      console.log('[Screenshot] No violations - returning early');
      return;
    }

    const violationsWithoutScreenshots = violations.filter(v => !v.screenshotUrl);
    console.log('[Screenshot] Violations without screenshots:', violationsWithoutScreenshots.length);

    if (violationsWithoutScreenshots.length === 0) {
      console.log('[Screenshot] All violations already have screenshots - showing alert');
      alert('All violations already have screenshots!');
      return;
    }

    const confirmed = confirm(
      `Capture screenshots for ${violationsWithoutScreenshots.length} violations?\n\n` +
      `This will take approximately ${Math.ceil(violationsWithoutScreenshots.length * 3 / 60)} minutes (3 seconds per screenshot due to rate limiting).`
    );

    console.log('[Screenshot] User confirmed:', confirmed);
    if (!confirmed) {
      console.log('[Screenshot] User cancelled - returning');
      return;
    }

    setCapturingScreenshots(true);
    setScreenshotProgress(`📸 Starting screenshot capture...`);
    console.log(`[Screenshot] Starting screenshot capture for batch: ${batchId}`);
    console.log(`[Screenshot] ${violationsWithoutScreenshots.length} violations need screenshots`);
    console.log(`[Screenshot] Estimated time: ${Math.ceil(violationsWithoutScreenshots.length * 3 / 60)} minutes (3s/screenshot due to rate limiting)`);

    try {
      console.log('[Screenshot] Calling backend action...');
      const result = await captureScreenshots({ batchId });
      console.log('[Screenshot] Backend action completed:', result);

      if (result.success) {
        console.log(`[Screenshot] ✅ Success! Captured: ${result.captured}, Failed: ${result.failed}, Skipped: ${result.skipped}`);
        setScreenshotProgress(`✅ Complete: ${result.captured} captured, ${result.failed} failed, ${result.skipped} already had screenshots`);
        setTimeout(() => {
          setScreenshotProgress('');
          setCapturingScreenshots(false);
        }, 5000);
      } else {
        console.error('[Screenshot] ❌ Backend returned error:', result.error);
        setScreenshotProgress(`❌ Error: ${result.error}`);
        setTimeout(() => {
          setScreenshotProgress('');
          setCapturingScreenshots(false);
        }, 5000);
      }
    } catch (error: any) {
      console.error('[Screenshot] ❌ Exception caught:', error);
      setScreenshotProgress(`❌ Error: ${error.message}`);
      setTimeout(() => {
        setScreenshotProgress('');
        setCapturingScreenshots(false);
      }, 5000);
    }
  };

  const formatPhaseLabel = (phase: string) => {
    const raw = phase.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
    if (!raw) return phase;
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  };

  const scanningScan = scansSafe.find(s => s.status === 'scanning');
  const allExpanded = scansSafe.length > 0 && scansSafe.every(s => expanded[s._id]);
  const toggleExpandAll = () => setAllExpanded(!allExpanded);
  const batchStatus = scansSafe.some(s => s.status === 'scanning')
    ? 'scanning'
    : scansSafe.some(s => s.status === 'error')
      ? 'error'
      : scansSafe.every(s => s.status === 'complete')
        ? 'complete'
        : 'inprogress';
  const batchStatusLabel = batchStatus === 'scanning' ? 'Scanning' : batchStatus === 'error' ? 'Attention' : batchStatus === 'complete' ? 'Complete' : 'In progress';

  const visibilityRecords = Array.isArray(visibilityByBatch)
    ? (visibilityByBatch as any[]).flat ? (visibilityByBatch as any[]).flat() : ([] as any[]).concat(...(visibilityByBatch as any[]))
    : [];
  const piratesBeforeOsnAvg = visibilityRecords.length > 0
    ? visibilityRecords.reduce((sum, v) => sum + (v.piratesBeforeOsn ?? 0), 0) / visibilityRecords.length
    : null;

  const searchTermLower = searchTerm.trim().toLowerCase();

  return (
    <>
    <div className="dashboard">
      <header className="command-bar">
        <div className="command-info">
          <div className="command-title-row">
            <h2>Scan Results</h2>
            <span className={`status-chip status-${batchStatus}`}>{batchStatusLabel}</span>
          </div>
          <div className="command-subtitle">
            {scansSafe.length} title{scansSafe.length === 1 ? '' : 's'} ·
            {totalViolations > 0 ? (
              <>
                {totalViolations} violation{totalViolations === 1 ? '' : 's'}
                {verifiedCount > 0 && unverifiedCount > 0 && (
                  <> ({verifiedCount} verified, {unverifiedCount} unverified)</>
                )}
              </>
            ) : (
              <> {totalViolations} violations</>
            )}
            {errorScans > 0 && <span className="subtitle-dot">· {errorScans} error{errorScans === 1 ? '' : 's'}</span>}
          </div>
          {scanningScan && (
            <div className="command-progress">
              <span className="phase">{formatPhaseLabel(scanningScan.currentPhase || 'Processing')}</span>
              {typeof scanningScan.progressCurrent === 'number' && typeof scanningScan.progressTotal === 'number' && scanningScan.progressTotal > 0 && (
                <span className="count">{scanningScan.progressCurrent}/{scanningScan.progressTotal}</span>
              )}
            </div>
          )}
        </div>
        <div className="command-actions">
          <Button
            onClick={handleCaptureScreenshots}
            disabled={capturingScreenshots || !violations || violations.length === 0}
            className="inline-flex items-center bg-primary text-primary-foreground px-4 py-2 rounded-md border border-transparent"
          >
            {capturingScreenshots ? '📸 Capturing…' : '📸 Capture Screenshots'}
          </Button>
          {batchStatus === 'scanning' && (
            <Button variant="secondary" onClick={togglePaused} className="inline-flex items-center border px-4 py-2 rounded-md bg-background hover:bg-accent">
              {pausedState ? '▶ Resume' : '⏸ Pause'}
            </Button>
          )}
          <Button variant="secondary" onClick={toggleExpandAll} className="inline-flex items-center border px-4 py-2 rounded-md bg-background hover:bg-accent">
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
          {filterScanId && (
            <Button variant="outline" onClick={() => setFilterScanId(null)}>
              Show entire batch
            </Button>
          )}
          <ExportButton batchId={batchId} scans={scans} violations={violations || []} />
          <Button variant="destructive" onClick={handleDeleteBatch} title="Delete this entire scan batch permanently" className="inline-flex items-center bg-destructive text-destructive-foreground px-4 py-2 rounded-md border border-transparent">
            🗑️ Delete Batch
          </Button>
        </div>
      </header>

      {screenshotProgress && (
        <div className={`screenshot-progress ${capturingScreenshots ? 'in-progress' : 'complete'}`}>
          {screenshotProgress}
        </div>
      )}

      <div className="filter-toolbar">
        <div className="filter-groups">
          <div className="filter-group">
            <span className="filter-heading">Crawl Status</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={`filter-pill ${filters.serverVerified ? 'active' : ''}`}
                  aria-pressed={filters.serverVerified}
                  onClick={() => toggleSimpleFilter('serverVerified')}
                >
                  Crawled ✓
                </button>
              </TooltipTrigger>
              <TooltipContent>
                Server successfully crawled and verified the piracy content
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={`filter-pill ${filters.serverUnverified ? 'active' : ''}`}
                  aria-pressed={filters.serverUnverified}
                  onClick={() => toggleSimpleFilter('serverUnverified')}
                >
                  Not Crawled ✗
                </button>
              </TooltipTrigger>
              <TooltipContent>
                Detected from search results only; site was unreachable during scan
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="filter-group">
            <span className="filter-heading">MENA summary</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={`filter-pill ${filters.regionalAnyReachable ? 'active' : ''}`}
                  aria-pressed={filters.regionalAnyReachable}
                  onClick={() => toggleSimpleFilter('regionalAnyReachable')}
                >
                  Any ✓
                </button>
              </TooltipTrigger>
              <TooltipContent>
                Site is reachable in at least one MENA region (SA, AE, or EG)
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={`filter-pill ${filters.regionalAllBlocked ? 'active' : ''}`}
                  aria-pressed={filters.regionalAllBlocked}
                  onClick={() => toggleSimpleFilter('regionalAllBlocked')}
                >
                  All ✗
                </button>
              </TooltipTrigger>
              <TooltipContent>
                Site is blocked in all MENA regions (SA, AE, and EG)
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="filter-group region-group">
            <span className="filter-heading">Per country</span>
            {REGION_CODES.map(code => (
              <div key={code} className="region-filter-row">
                <span className="region-label">{code.toUpperCase()}</span>
                <button
                  type="button"
                  className={`filter-pill tiny ${filters.regionalPerCountryReachable[code] ? 'active' : ''}`}
                  aria-pressed={filters.regionalPerCountryReachable[code]}
                  onClick={() => toggleRegionalReachable(code)}
                  title={`Require reachable in ${code.toUpperCase()}`}
                >
                  ✓
                </button>
                <button
                  type="button"
                  className={`filter-pill tiny ${filters.regionalPerCountryBlocked[code] ? 'active' : ''}`}
                  aria-pressed={filters.regionalPerCountryBlocked[code]}
                  onClick={() => toggleRegionalBlocked(code)}
                  title={`Require blocked in ${code.toUpperCase()}`}
                >
                  ✗
                </button>
              </div>
            ))}
          </div>

          {/* Domain Filter Dropdown - moved to filter groups */}
          {domainCounts.length > 0 && (
            <div className="filter-group">
              <span className="filter-heading">Filter Domains</span>
              <div className="domain-filter-dropdown-inline" aria-label="domain filters">
                <button
                  className="domain-dropdown-toggle"
                  onClick={() => setDomainDropdownOpen(!domainDropdownOpen)}
                >
                  <span className="domain-dropdown-label">
                    {domainFilter ? (
                      <>
                        <span className="domain-name">{domainFilter}</span>
                        <span className="domain-count">
                          ({domainCounts.find(([d]) => d === domainFilter)?.[1] || 0})
                        </span>
                      </>
                    ) : (
                      <>
                        All Domains ({domainCounts.reduce((s, [,c]) => s + c, 0)})
                      </>
                    )}
                  </span>
                  <span className={`caret ${domainDropdownOpen ? 'open' : ''}`}>▼</span>
                </button>

                {domainDropdownOpen && (
                  <div className="domain-dropdown-panel">
                    <button
                      className={`domain-option ${!domainFilter ? 'active' : ''}`}
                      onClick={() => {
                        setDomainFilter(null);
                        setDomainDropdownOpen(false);
                      }}
                    >
                      <span className="domain-name">All Domains</span>
                      <span className="domain-count">{domainCounts.reduce((s, [,c]) => s + c, 0)}</span>
                    </button>
                    {domainCounts
                      .filter(([d]) => !searchTermLower || d.includes(searchTermLower))
                      .slice(0, 50)
                      .map(([d, c]) => (
                        <button
                          key={d}
                          className={`domain-option ${domainFilter === d ? 'active' : ''}`}
                          onClick={() => {
                            setDomainFilter(prev => prev === d ? null : d);
                            setDomainDropdownOpen(false);
                          }}
                        >
                          <span className="domain-name">{d}</span>
                          <span className="domain-count">{c}</span>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="filter-side">
          <div className="filter-search">
            <input
              type="search"
              placeholder="Search domains or URLs"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-actions">
            <Button variant="outline" onClick={resetFilters} disabled={!isFilterActive}>
              Reset filters
            </Button>
          </div>
        </div>
      </div>

      <div className="reachability-legend" aria-label="Reachability legend">
        <span className="legend-item">
          <span className="regional-badge reachable">SA ✓</span>
          <span className="legend-text">Reachable in Saudi Arabia</span>
        </span>
        <span className="legend-item">
          <span className="regional-badge blocked">AE ✗</span>
          <span className="legend-text">Blocked in United Arab Emirates</span>
        </span>
        <span className="legend-item">
          <span className="regional-badge reachable">EG ✓</span>
          <span className="legend-text">Reachable in Egypt</span>
        </span>
        <span className="legend-item">
          <span className="regional-badge error">EG !</span>
          <span className="legend-text">Proxy error (retry scheduled)</span>
        </span>
        <span className="legend-item">
          <span className="regional-badge unknown">MENA ?</span>
          <span className="legend-text">No proxy data yet</span>
        </span>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Titles scanned</div>
          <div className="stat-value">{scansSafe.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Violations found</div>
          <div className="stat-value">{totalViolations}</div>
        </div>
        <div className={`stat-card ${avgVisibilityScore !== null ? getScoreColorClass(avgVisibilityScore) : ''}`}>
          <div className="stat-label">Avg visibility score</div>
          <div className="stat-value">
            {avgVisibilityScore !== null ? avgVisibilityScore.toFixed(1) : 'N/A'}
          </div>
          {avgVisibilityScore !== null && (
            <div className="stat-sublabel">{getScoreInterpretation(avgVisibilityScore)}</div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-label">Pirates before OSN+</div>
          <div className="stat-value">
            {piratesBeforeOsnAvg !== null ? piratesBeforeOsnAvg.toFixed(1) : 'N/A'}
          </div>
        </div>
      </div>

      <div className="scans-list">
        <h3>Content Scans</h3>
        {filteredScans.map((scan) => (
          <div key={scan._id} id={`scan-${scan._id}`} className="scan-item card">
            <div className="scan-header">
              <div className="scan-info">
                <h4>{scan.contentTitle}</h4>
                <div className="scan-meta">
                  {scan.contentYear && <span>{scan.contentYear}</span>}
                  {scan.contentType && <span className="badge">{scan.contentType}</span>}
                  {scan.priority && (
                    <span className={`badge badge-${scan.priority}`}>
                      {scan.priority} priority
                    </span>
                  )}
                  {(scan.completedAt || scan.startedAt) && (
                    <span className="scan-timestamp">
                      {scan.status === 'complete' && scan.completedAt
                        ? `Scanned ${new Date(scan.completedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}`
                        : `Started ${new Date(scan.startedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}`
                      }
                    </span>
                  )}
                </div>
              </div>

              {/* Center: compact status summary */}
              <div className="status-summary">
                {scan.status === 'scanning' ? (
                  expanded[scan._id] ? null : (
                    <div className="scanning-progress">
                      <span className="status-scanning-active">⚡ SCANNING</span>
                      {scan.currentPhase && (
                        <span className="scan-phase">{scan.currentPhase}</span>
                      )}
                      {scan.progressCurrent !== undefined && scan.progressTotal !== undefined && (
                        <span className="scan-progress-count">{scan.progressCurrent}/{scan.progressTotal}</span>
                      )}
                    </div>
                  )
                ) : (
                  <span className={`status-${scan.status}`}>{scan.status.toUpperCase()}</span>
                )}

                {scan.status === 'complete' && (
                  <>
                    <span className="violation-count compact">
                      {scan.violationCount} violation{scan.violationCount !== 1 ? 's' : ''}
                    </span>
                    {scan.visibilityScore !== undefined && scan.visibilityScore !== null && (
                      <span className={`visibility-score-badge ${getScoreColorClass(scan.visibilityScore)}`} title={getScoreInterpretation(scan.visibilityScore)}>
                        📊 {scan.visibilityScore.toFixed(1)}/100
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Right: actions */}
              <div className="scan-actions">
                <a className="back-to-index" href="#results-index" title="Back to results index">↑ Top</a>
                <button
                  className="btn-tertiary"
                  onClick={() => setExpanded(prev => ({ ...prev, [scan._id]: !prev[scan._id] }))}
                >
                  {expanded[scan._id] ? 'Collapse' : 'Expand'}
                </button>
                <button
                  className="btn-tertiary"
                  onClick={() => setFilterScanId(filterScanId === scan._id ? null : scan._id)}
                  title={filterScanId === scan._id ? "Show all titles" : "Show only this title"}
                >
                  {filterScanId === scan._id ? 'Show All' : 'Show Only'}
                </button>
              </div>
            </div>

            {scan.status === 'scanning' && expanded[scan._id] && (
              <div className="scan-progress-details">
                <div className="phase-list" role="status" aria-label="scan phases">
                  {[
                    { key: 'searching', label: '1. SEARCHING', desc: 'Finding potential piracy sources' },
                    { key: 'scraping', label: '2. SCRAPING', desc: 'Extracting content from URLs' },
                    { key: 'classifying', label: '3. CLASSIFYING', desc: 'AI validation of piracy' },
                    { key: 'hostingLookup', label: '4. HOSTING LOOKUP', desc: 'Identifying domain hosting providers' },
                    { key: 'visibility', label: '5. VISIBILITY SCORING', desc: 'Calculating search visibility' },
                    { key: 'finalizing', label: '6. FINALIZING', desc: 'Saving analytics & results' },
                  ].map((phase) => {
                    const isActive = scan.currentPhase === phase.key;
                    const isComplete = false; // We don't track completion per phase yet, could add later
                    const icon = isActive ? '⚡' : (isComplete ? '✅' : '⏳');

                    return (
                      <div key={phase.key} className={`phase-item ${isActive ? 'active' : ''}`}>
                        <div className="phase-header">
                          <span className="phase-icon">{icon}</span>
                          <span className="phase-label">{phase.label}</span>
                        </div>
                        {isActive && scan.progressText && (
                          <div className="phase-activity">
                            {scan.progressText}
                          </div>
                        )}
                        {isActive && scan.progressCurrent !== undefined && scan.progressTotal !== undefined && (
                          <div className="phase-progress-bar">
                            <div
                              className="phase-progress-fill"
                              style={{ width: `${Math.min(100, Math.round((scan.progressCurrent / scan.progressTotal) * 100))}%` }}
                            />
                            <span className="phase-progress-label">
                              {scan.progressCurrent}/{scan.progressTotal} ({Math.round((scan.progressCurrent / scan.progressTotal) * 100)}%)
                            </span>
                          </div>
                        )}
                        {!isActive && (
                          <div className="phase-desc">{phase.desc}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {scan.progressCurrent !== undefined && scan.progressTotal !== undefined && (() => {
                  const now = Date.now();
                  const key = `${scan._id}:${scan.currentPhase || 'unknown'}`;
                  // initialize phase start
                  // @ts-ignore
                  if (!window.__phaseStart) window.__phaseStart = {};
                  // @ts-ignore
                  const phaseStart = window.__phaseStart[key] || (window.__phaseStart[key] = now);
                  // @ts-ignore
                  if (!window.__lastProg) window.__lastProg = {};
                  // @ts-ignore
                  const last = window.__lastProg[key] || { t: now, c: scan.progressCurrent };
                  const dtSec = Math.max(1, (now - last.t) / 1000);
                  const dc = Math.max(0, scan.progressCurrent - last.c);
                  const ratePerMin = (dc / dtSec) * 60;
                  // @ts-ignore
                  window.__lastProg[key] = { t: now, c: scan.progressCurrent };
                  const remaining = Math.max(0, scan.progressTotal - scan.progressCurrent);
                  const etaMin = ratePerMin > 0 ? remaining / ratePerMin : null;
                  const pct = scan.progressTotal > 0 ? Math.round((scan.progressCurrent / scan.progressTotal) * 100) : 0;
                  return (
                    <div className="progress-rich">
                      <div className="progress-bar-container inline" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="phase progress">
                        <div className="progress-bar-fill" style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                      <div className="progress-meta">
                        <span className="metric">{scan.progressCurrent}/{scan.progressTotal} ({pct}%)</span>
                        {ratePerMin > 0.1 && (
                          <>
                            <span className="dot">•</span>
                            <span className="metric">{ratePerMin.toFixed(1)}/min</span>
                          </>
                        )}
                        {etaMin !== null && isFinite(etaMin) && ratePerMin > 0.1 && (
                          <>
                            <span className="dot">•</span>
                            <span className="metric">ETA ~{Math.max(1, Math.round(etaMin))}m</span>
                          </>
                        )}
                        {scan.currentPhase === 'scraping' && remaining > 0 && (
                          <>
                            <span className="dot">•</span>
                            <span className="metric">Remaining {remaining}</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {/* Recent scraped domains */}
                {scan.currentPhase === 'scraping' && extractRecentDomains(scan).length > 0 && (
                  <div className="recent-items" aria-label="recent domains">
                    {extractRecentDomains(scan).map((d, i) => (
                      <span key={`${d}-${i}`} className="recent-chip">{d}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {expanded[scan._id] && scan.status === 'complete' && scan.visibilityScore !== undefined && scan.visibilityScore !== null && (
              <div className="visibility-details">
                <div className="visibility-metrics">
                  <div className="visibility-metric">
                    <span className="metric-label">Search Visibility:</span>
                    <span className={`metric-value ${getScoreColorClass(scan.visibilityScore)}`}>
                      {scan.visibilityScore.toFixed(1)}/100 - {getScoreInterpretation(scan.visibilityScore)}
                    </span>
                  </div>
                  {scan.osnRank && (
                    <div className="visibility-metric">
                      <span className="metric-label">OSN+ Rank:</span>
                      <span className="metric-value">#{scan.osnRank}</span>
                    </div>
                  )}
                  {scan.piratesBeforeOsn !== undefined && scan.piratesBeforeOsn !== null && (
                    <div className="visibility-metric">
                      <span className="metric-label">Pirates Before OSN+:</span>
                      <span className="metric-value">{scan.piratesBeforeOsn}</span>
                    </div>
                  )}
                </div>

                {/* SQS breakdown */}
                {(() => {
                  const vds = (visibilityByBatch || []).filter((v: any) => String(v.scanId) === String(scan._id));
                  if (!vds || vds.length === 0) return null; // hide chart entirely when no per-query data
                  const sorted = [...vds].sort((a,b) => b.visibilityScore - a.visibilityScore);
                  const best = sorted[0];
                  const worst = sorted[sorted.length - 1];
                  const scores = vds.map(v => v.visibilityScore);

                  // Use actual score range for better visualization
                  const maxS = Math.max(...scores, 10); // minimum range of 10 for visibility
                  const minS = Math.min(...scores);
                  const range = maxS - minS;

                  // Add padding to prevent edge clipping
                  const points = scores.map((s, i) => {
                    const x = 5 + (i / Math.max(1, scores.length - 1)) * 90;
                    // If all scores are the same, center the line vertically
                    const y = range === 0 ? 50 : 95 - ((s - minS) / range) * 85;
                    return `${x},${y}`;
                  }).join(' ');

                  // Build provenance tooltips
                  const buildProvenance = (vd: any) => {
                    const lines = [vd.query];
                    if (vd.osnUrls && vd.osnUrls.length > 0) {
                      const osnInfo = vd.osnUrls.map((u: any) => `  #${u.rank} (${u.source || '?'})`).join(', ');
                      lines.push(`OSN+: ${osnInfo}`);
                    }
                    if (vd.pirateUrls && vd.pirateUrls.length > 0) {
                      const pirateInfo = vd.pirateUrls.slice(0, 3).map((u: any) => `#${u.rank} ${u.domain} (${u.source || '?'})`).join(', ');
                      const more = vd.pirateUrls.length > 3 ? ` +${vd.pirateUrls.length - 3} more` : '';
                      lines.push(`Pirates: ${pirateInfo}${more}`);
                    }
                    return lines.join('\n');
                  };

                  return (
                    <div className="sqs-breakdown">
                      <div className="sqs-row">
                        <span className="metric-label">Best Query:</span>
                        <span className="metric-value" title={buildProvenance(best)}>{best.visibilityScore.toFixed(1)} — {best.query.slice(0, 60)}{best.query.length>60?'…':''}</span>
                      </div>
                      <div className="sqs-row">
                        <span className="metric-label">Worst Query:</span>
                        <span className="metric-value" title={buildProvenance(worst)}>{worst.visibilityScore.toFixed(1)} — {worst.query.slice(0, 60)}{worst.query.length>60?'…':''}</span>
                      </div>
                      <div className="sparkline" aria-label="SQS sparkline">
                        <svg viewBox="0 0 100 100" preserveAspectRatio="none">
                          {/* Reference line at y=50 for context */}
                          <line x1="5" y1="50" x2="95" y2="50" stroke="#e5e7eb" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
                          <polyline fill="none" stroke="#2563eb" strokeWidth="2" vectorEffect="non-scaling-stroke" points={points} />
                          {scores.map((s, i) => {
                            const x = 5 + (i / Math.max(1, scores.length - 1)) * 90;
                            const y = range === 0 ? 50 : 95 - ((s - minS) / range) * 85;
                            return <circle key={i} cx={x} cy={y} r="2" fill="#2563eb" vectorEffect="non-scaling-stroke" />;
                          })}
                        </svg>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {expanded[scan._id] && scan.status === 'error' && scan.errorMessage && (
              <div className="error-message">
                Error: {scan.errorMessage}
              </div>
            )}

            {/* Bulk toolbar for this scan */}
            {expanded[scan._id] && (
              <div className="bulk-toolbar">
                <label className="show-archived">
                  <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show Archived
                </label>
                <div className="bulk-actions">
                  <Button
                    variant="outline"
                    size="sm"
                    className="inline-flex items-center px-3 rounded-md border"
                    title="Select all high‑confidence items (>=80%) in this scan"
                    onClick={() => {
                      const items = currentViolations(scan._id).filter((v: any) => (v.confidenceScore || 0) >= 80);
                      setSelectedIds(prev => {
                        const next = new Set(prev);
                        for (const it of items) next.add(it._id);
                        return next;
                      });
                    }}
                  >
                    Select High‑Confidence
                  </Button>
                  {selectedIds.size > 0 && (
                    <>
                      <span className="bulk-count">Selected: {selectedIds.size}</span>
                      <BulkTakedownWizardTrigger
                        violations={currentViolations(scan._id).filter((v: any) => selectedIds.has(v._id))}
                      />
                      <Button
                        variant="outline"
                        className="border px-3 rounded-md"
                        onClick={async () => {
                          if (!confirm(`Mark ${selectedIds.size} item(s) as Not Pirate?`)) return;
                          for (const id of Array.from(selectedIds)) {
                            await bulkMarkNotPirate({ violationId: id });
                          }
                          setSelectedIds(new Set());
                        }}
                      >
                        ✖ Mark Not Pirate
                      </Button>
                      <Button
                        variant="outline"
                        className="border px-3 rounded-md"
                        onClick={async () => {
                          if (!confirm(`Restore ${selectedIds.size} archived item(s)?`)) return;
                          for (const id of Array.from(selectedIds)) {
                            await bulkRestore({ violationId: id });
                          }
                          setSelectedIds(new Set());
                        }}
                      >
                        ↺ Restore
                      </Button>
                      <Button
                        variant="outline"
                        className="border px-3 rounded-md"
                        onClick={() => {
                          // Ensure all selected are same domain
                          const all = currentViolations(scan._id);
                          const selected = all.filter(v => selectedIds.has(v._id));
                          const domains = Array.from(new Set<string>(selected.map((v: any) => String(v.domain))));
                          if (domains.length !== 1) {
                            alert('Please select violations from a single domain to send a takedown.');
                            return;
                          }
                          setDmcaDomain(domains[0] as string);
                        }}
                      >
                        ⚖️ Send Takedown
                      </Button>
                      <Button variant="outline" className="border px-3 rounded-md" onClick={() => setSelectedIds(new Set())}>Clear</Button>
                    </>
                  )}
                </div>
              </div>
            )}

            {expanded[scan._id] && scan.status === 'complete' && (
              <div className="violations-section">
                {(() => {
                  const list = currentViolations(scan._id);
                  const byDomain: Record<string, any[]> = {};
                  for (const v of list) {
                    const d = String(v.domain).toLowerCase();
                    (byDomain[d] ||= []).push(v);
                  }
                  const domains = Object.keys(byDomain).sort((a, b) => {
                    const ca = byDomain[a].length, cb = byDomain[b].length;
                    if (cb !== ca) return cb - ca;
                    const sa = Math.max(...byDomain[a].map(v => v.confidenceScore || 0));
                    const sb = Math.max(...byDomain[b].map(v => v.confidenceScore || 0));
                    return sb - sa;
                  });

                  const domainPasses = (domain: string) => {
                    const items = byDomain[domain];
                    const anyVerified = items.some((v: any) => v.verified === true);
                    const anyUnverified = items.some((v: any) => v.verified === false || v.verificationMethod === 'search_only');
                    const reachVal = (() => { try { return sessionStorage.getItem(`reach:${domain}`); } catch { return null; } })();
                    const isReach = reachVal === 'reachable';
                    const isUnreach = reachVal === 'unreachable';
                    const regionStatus: Record<RegionCode, RegionStatus> = {
                      sa: deriveRegionStatus(items, 'reachableSAStatus'),
                      ae: deriveRegionStatus(items, 'reachableAEStatus'),
                      eg: deriveRegionStatus(items, 'reachableEGStatus'),
                    };
                    const anyRegionReachable = REGION_CODES.some(code => regionStatus[code] === 'reachable');
                    const allRegionsBlocked = REGION_CODES.every(code => regionStatus[code] === 'blocked');

                    if (filters.clientReachable || filters.clientUnreachable) {
                      if (!((filters.clientReachable && isReach) || (filters.clientUnreachable && isUnreach))) {
                        return false;
                      }
                    }

                    if (filters.serverVerified || filters.serverUnverified) {
                      if (!((filters.serverVerified && anyVerified) || (filters.serverUnverified && anyUnverified))) {
                        return false;
                      }
                    }

                    if (filters.regionalAnyReachable && !anyRegionReachable) return false;
                    if (filters.regionalAllBlocked && !allRegionsBlocked) return false;

                    for (const code of REGION_CODES) {
                      if (filters.regionalPerCountryReachable[code] && regionStatus[code] !== 'reachable') return false;
                      if (filters.regionalPerCountryBlocked[code] && regionStatus[code] !== 'blocked') return false;
                    }

                    return true;
                  };
                  return domains
                    .filter(domainPasses)
                    .filter((domain) => {
                      if (!searchTermLower) return true;
                      if (domain.includes(searchTermLower)) return true;
                      return byDomain[domain].some((v: any) => String(v.url || '').toLowerCase().includes(searchTermLower));
                    })
                    .map((domain) => {
                    const items = byDomain[domain];
                    const count = items.length;
                    const topScore = Math.max(...items.map(v => v.confidenceScore || 0));
                    const anyHosting = items.find(v => v.hostingProvider || v.ipAddress);
                    const groupKey = `${scan._id}:${domain}`;
                    const isOpen = expanded[groupKey] ?? false;
                    const topUrls = items.slice(0, 3).map(v => v.url);
                    const regionStatusMap: Record<RegionCode, RegionStatus> = {
                      sa: deriveRegionStatus(items, 'reachableSAStatus'),
                      ae: deriveRegionStatus(items, 'reachableAEStatus'),
                      eg: deriveRegionStatus(items, 'reachableEGStatus'),
                    };
                    return (
                      <div key={groupKey} className="domain-group card">
                        <div className="domain-group-header">
                          <div className="left">
                            <span
                              className="domain-badge"
                              onClick={() => setDomainFilter(prev => prev === domain ? null : domain)}
                              title="Click to filter by this domain"
                              role="button"
                            >
                              {count >= 5 && <span title="Serial Offender (5+ violations)">☠️ </span>}
                              {count >= 2 && count < 5 && <span title="Repeat Offender (2+ violations)">🔥 </span>}
                              {domain}
                            </span>
                            <span className="domain-count">{count} result{count !== 1 ? 's' : ''}</span>
                            <span className="domain-score">Top {Math.round(topScore)}%</span>
                            {anyHosting?.hostingProvider && (
                              <span className="domain-host">{anyHosting.hostingProvider}</span>
                            )}
                            {/* Client reachability quick badge for domain */}
                            <ReachabilityBadge host={domain} />
                            <RegionalBadges
                              statusMap={{
                                sa: regionStatusMap.sa,
                                ae: regionStatusMap.ae,
                                eg: regionStatusMap.eg,
                              }}
                              size="compact"
                            />
                          </div>
                          <div className="right">
                            <Button variant="outline" size="sm" onClick={() => {
                              // select all in this domain
                              setSelectedIds(prev => {
                                const next = new Set(prev);
                                const allIds = items.map(v => v._id);
                                const hasAll = allIds.every(id => next.has(id));
                                if (hasAll) { allIds.forEach(id => next.delete(id)); } else { allIds.forEach(id => next.add(id)); }
                                return next;
                              });
                            }}>Select {items.every(v => selectedIds.has(v._id)) ? 'None' : 'All'}</Button>
                            <Button variant="outline" size="sm" onClick={() => setDmcaDomain(domain)}>⚖️ Send Takedown</Button>
                            <Button variant="outline" size="sm" onClick={() => setExpanded(prev => ({ ...prev, [groupKey]: !isOpen }))}>{isOpen ? 'Hide' : 'Expand'}</Button>
                          </div>
                        </div>
                        <div className="domain-group-body">
                          <div className="url-chips">
                            {topUrls.map((u, i) => (
                              <a key={`${u}-${i}`} href={u} target="_blank" rel="noopener noreferrer" className="url-chip">{getReadablePath(u)}</a>
                            ))}
                            {count > 3 && <span className="more-chip">+{count - 3} more</span>}
                          </div>
                          {isOpen && (
                            <div className="domain-violations">
                              <ProgressiveList
                                items={items}
                                initial={50}
                                step={50}
                                renderItem={(violation: any) => (
                                  <ViolationCard
                                    key={violation._id}
                                    violation={violation}
                                    contentTitle={scan.contentTitle}
                                    onToggleSelect={toggleSelected}
                                    isSelected={selectedIds.has(violation._id)}
                                  />
                                )}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
    {dmcaDomain && (
      <DMCAEmailModal domain={dmcaDomain} onClose={() => setDmcaDomain(null)} />
    )}
    </>
  );
}

function BulkTakedownWizardTrigger({ violations }: { violations: any[] }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)} title="Prepare bulk DMCA takedowns">⚖️ Bulk Takedown</Button>
      {open && (
        <BulkTakedownWizard violations={violations} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function pickRecipient(v: any): { email: string | null; type: string | null } {
  if (v.hostingAbuseEmail) return { email: v.hostingAbuseEmail, type: 'hosting' };
  if (v.registrarAbuseEmail) return { email: v.registrarAbuseEmail, type: 'registrar' };
  if (v.abuseEmail) return { email: v.abuseEmail, type: 'domain' };
  if (v.abuseEmails && v.abuseEmails.length > 0) return { email: v.abuseEmails[0], type: 'domain' };
  return { email: null, type: null };
}

function BulkTakedownWizard({ violations, onClose }: { violations: any[]; onClose: () => void }) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<any[] | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const generateEmail = useAction(api.dmca.generateDMCAEmail);
  const sendEmail = useAction(api.dmca.sendDMCAEmail);

  // Group by domain
  const byDomain = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const v of violations) {
      const d = String(v.domain).toLowerCase();
      map.set(d, [...(map.get(d) || []), v]);
    }
    return Array.from(map.entries());
  }, [violations]);

  const loadPreviews = async () => {
    setError(null);
    try {
      const pv: any[] = [];
      for (const [domain, items] of byDomain) {
        const r = pickRecipient(items[0]);
        if (!r.email) {
          pv.push({ domain, error: 'No recipient email found' });
          continue;
        }
        const tmpl = await generateEmail({ domain, recipientEmail: r.email, recipientType: r.type || 'domain' });
        pv.push({ domain, recipient: r, template: tmpl });
      }
      setPreviews(pv);
      const sel: Record<string, boolean> = {};
      pv.forEach(p => sel[p.domain] = !p.error);
      setSelected(sel);
    } catch (e: any) {
      setError(e.message || 'Failed to generate emails');
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      for (const p of previews || []) {
        if (p.error) continue;
        if (!selected[p.domain]) continue;
        await sendEmail({ domain: p.domain, recipientEmail: p.recipient.email, recipientType: p.recipient.type || 'domain' });
      }
      onClose();
      alert('Bulk takedowns sent');
    } catch (e: any) {
      setError(e.message || 'Failed to send some emails');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="evidence-overlay" onClick={onClose}>
      <div className="evidence-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="evidence-header">
          <div>
            <div className="evidence-title">Bulk Takedown Wizard</div>
            <div className="evidence-link">{violations.length} selected items · {byDomain.length} domains</div>
          </div>
          <button className="evidence-close" onClick={onClose}>✕</button>
        </div>
        <div className="evidence-body">
          {!previews ? (
            <div>
              <p>Prepare takedown drafts for each domain. The system will pick hosting/registrar emails when available.</p>
              <Button onClick={loadPreviews}>Generate Drafts</Button>
              {error && <div className="hosting-info-error" style={{ marginTop: 8 }}><strong>Error:</strong> {error}</div>}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {previews.map((p: any) => (
                <div key={p.domain} className="hosting-info" style={{ background: '#fff', borderColor: '#e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <strong>{p.domain}</strong>
                      <div style={{ fontSize: '.9rem', color: '#6b7280' }}>
                        {p.error ? p.error : `To: ${p.recipient.email} (${p.recipient.type})`}
                      </div>
                    </div>
                    <label>
                      <input type="checkbox" checked={!!selected[p.domain]} onChange={(e)=>setSelected({ ...selected, [p.domain]: e.target.checked })} /> Include
                    </label>
                  </div>
                  {!p.error && (
                    <details style={{ marginTop: 8 }}>
                      <summary>Preview</summary>
                      <div style={{ marginTop: 8 }} dangerouslySetInnerHTML={{ __html: p.template.html }} />
                    </details>
                  )}
                </div>
              ))}
              <div>
                <Button onClick={handleSend} disabled={sending}>{sending ? 'Sending…' : 'Send All'}</Button>
                {error && <div className="hosting-info-error" style={{ marginTop: 8 }}><strong>Error:</strong> {error}</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
